import React, { useCallback, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import { patientAccessTheme } from '@HomaHealth/ui-component-library';
import {
  Button,
  TextField,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import ProgressBar from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/ProgressBar';
import Upload from '../../../../public/Images/Upload Document.png';
import { useDropzone } from 'react-dropzone';
import {
  FileSizeFormatter,
  isValidURL,
} from '../../../core-utils/helper-utils/helper';
import Delete from '../../atoms/Icons/Delete';
import File from '../../atoms/Icons/File';
import axios, { AxiosProgressEvent } from 'axios';
import properties from '../../../core-utils/properties';
import { useStores } from '../../../stores';
import { FileUploaderConstants } from '../../../core-utils/messages/FileUploaderConstants';

export interface FileUploaderProps {
  activeTabIndex: number;
  activeTabName: string;
  isFileDetailsNeeded?: boolean;
  isFileLinkUploadSupported?: boolean;
  fileTypeSupported: string[];
   
  handleStageCompletion: (index: number) => void;
  handleOpenSnackBar: (
     
    message: string,
     
    severity: MuiColor,
     
    duration: number | null,
  ) => void;
   
  createNamespace?: (fileUrl: string) => void;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  hideReviewButton?: boolean;
  hideCancelButton?: boolean;
  handleOnUpload?: () => void;
}
export interface FileDetailsProps {
  fileName: string;
  fileSize: number;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  activeTabIndex,
  activeTabName,
  isFileDetailsNeeded = true,
  isFileLinkUploadSupported,
  fileTypeSupported,
  handleStageCompletion,
  handleOpenSnackBar,
  createNamespace,
  setLoading,
  hideCancelButton,
  hideReviewButton,
  handleOnUpload,
}) => {
  const [fileUploadProgress, setFileUploadProgress] = useState<number>(0);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [url, setUrl] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(false);
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadedFiles([...uploadedFiles, ...acceptedFiles]);
    },
    [uploadedFiles],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
      'application/pdf': ['.pdf'],
    },
    multiple: false,
    onDrop,
  });

  const useStyles = makeStyles()({
    actionArea: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: '1rem',
      flex: '90%',
      marginTop: '1rem',
    },
    dragDropArea: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '1.5rem 1rem',
      borderRadius: '0.25rem',
      width: '100%',
      minHeight: '180px',
      backgroundColor: patientAccessTheme.palette.grey[100],
      border: isDragActive
        ? `3px solid ${patientAccessTheme.palette.primary[500]} `
        : `2px dashed ${patientAccessTheme.palette.grey[500]} `,
    },
    separator: {
      width: '100%',
      height: '1px',
      backgroundColor: patientAccessTheme.palette.grey[300],
      border: 'none',
    },
    progressContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  });
  const { classes } = useStyles();
  const LoginPageStore = useStores().loginPageStore;
  const FileUploadStore = useStores().fileUploadStore;
  const messages = FileUploaderConstants;

  const FileDetails = ({ fileName, fileSize }: FileDetailsProps) => {
    const useStyles = makeStyles()({
      fileDetailsContainer: {
        backgroundColor: patientAccessTheme.palette.grey[100],
        border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        padding: '1rem',

        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      fileIconContainer: {
        display: 'flex',
        alignItems: 'center',
      },
      fileNameContainer: { marginLeft: '1.8rem' },
      fileName: {
        fontSize: '1rem',
        fontWeight: 400,
        color: patientAccessTheme.palette.text.mediumemphasis,
      },
      fileSize: {
        fontSize: '0.8rem',
        color: patientAccessTheme.palette.text.lowemphasis,
      },
      DeleteIcon: {
        cursor: 'pointer',
      },
    });
    const { classes } = useStyles();
    return (
      <Grid className={classes.fileDetailsContainer}>
        <Grid className={classes.fileIconContainer}>
          <File />
          <div className={classes.fileNameContainer}>
            <Typography variant="body2" className={classes.fileName}>
              {fileName === '' ? `Sample${fileTypeSupported[0]}` : fileName}
            </Typography>
            <Typography variant="body2" className={classes.fileSize}>
              {fileSize === 0 ? '0.00KB' : FileSizeFormatter(fileSize)}
            </Typography>
          </div>
        </Grid>

        <Delete className={classes.DeleteIcon} onClick={() => removeFile(0)} />
      </Grid>
    );
  };

  const FileLinkInput = () => {
    return (
      <Grid
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}
      >
        <Typography
          variant="subtitle1"
          style={{
            fontWeight: 500,
          }}
        >
          {messages.LINK_INPUT_TITLE}
        </Typography>
        <TextField
          value={url}
          placeholder={messages.LINK_INPUT_PLACEHOLDER}
          height="36px"
          width="600px"
          variant="outlined"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setIsValid(isValidURL(e.target.value));
            setUrl(e.target.value);
          }}
          endAdornment={
            <Button
              width=""
              variant="text"
              label={messages.UPLOAD}
              disabled={!isValid}
              onClick={handleSubmit}
            />
          }
        />
      </Grid>
    );
  };

  const onUploadProgressFunc = (progressEvent: AxiosProgressEvent) => {
    if (progressEvent.progress !== undefined) {
      setFileUploadProgress(Math.round(progressEvent.progress * 100));
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const formData = new FormData();

    if (uploadedFiles.length > 0) {
      formData.append('file', uploadedFiles[0]);
      formData.append('fileType', activeTabName);
      formData.append('userId', LoginPageStore.userId);
      formData.append('fileUploadType', messages.FILE);
    } else {
      formData.append('fileType', activeTabName);
      formData.append('fileLink', url);
      formData.append('fileUploadType', messages.LINK);
    }

    try {
      const response = await axios.post(
        properties.aiqBaseUrl + properties.onboarding,
        formData,
        {
          onUploadProgress: onUploadProgressFunc,
        },
      );

      if (response.status === 200) {
        setFileUploadProgress(0);
        handleOpenSnackBar(
          messages.UPLOAD_SUCCESS(activeTabName),
          'success',
          5000,
        );
        FileUploadStore.setUploadedFile(response.data?.data?.blobUrl);
        if (createNamespace && setLoading) {
          setLoading(true);
          await createNamespace(response.data?.data?.blobUrl);
        }
        if (handleOnUpload && setLoading) {
          setLoading(true);
          handleOnUpload();
        }
      }
      setUploadedFiles([]);
      setUrl('');
      handleStageCompletion(activeTabIndex);
    } catch (error) {
      handleOpenSnackBar(messages.UPLOAD_ERROR(activeTabName), 'error', 5000);
      setFileUploadProgress(0);
      setUploadedFiles([]);
      setUrl('');
    }
  };
  const removeFile = (index: number) => {
    setUploadedFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  return (
    <>
      <Grid className={classes.actionArea}>
        <Grid
          {...getRootProps({
            className: `dropzone ${classes.dragDropArea}`,
          })}
        >
          <img src={Upload} alt="file-upload" />
          <input {...getInputProps()} />
          {uploadedFiles.length > 0 ? (
            <Typography
              style={{
                marginTop: '1.5rem',
                fontWeight: 500,
                color: patientAccessTheme.palette.success.main,
              }}
            >
              {messages.DROP_AREA.AFTER_FILE_SELECTED}
            </Typography>
          ) : (
            <Typography
              style={{
                marginTop: '1.5rem',
                fontWeight: 500,
              }}
            >
              {messages.DROP_AREA.TITLE}
              <span
                style={{
                  color: patientAccessTheme.palette.primary[400],
                  cursor: 'pointer',
                }}
              >
                {messages.DROP_AREA.SUBTITLE2}
              </span>
            </Typography>
          )}

          <Typography
            style={{
              fontSize: '0.8rem',
              fontWeight: 400,
              color: patientAccessTheme.palette.grey[600],
            }}
          >
            ({messages.DROP_AREA.SUBTITLE(fileTypeSupported)})
          </Typography>
        </Grid>

        {isFileDetailsNeeded && uploadedFiles.length > 0
          ? uploadedFiles.map((file: any) => (
              <FileDetails fileName={file.name} fileSize={file.size} />
            ))
          : isFileDetailsNeeded && <FileDetails fileName="" fileSize={0} />}

        <hr className={classes.separator} />
        <Grid className={classes.progressContainer}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}
          >
            <ProgressBar value={fileUploadProgress} height="6px" />
            <Typography
              variant="body2"
              style={{
                fontSize: '0.8rem',
                fontWeight: 400,
                color: patientAccessTheme.palette.text.lowemphasis,
              }}
            >
              {fileUploadProgress}%
            </Typography>
          </div>
          <Button
            disabled={uploadedFiles.length > 0 ? false : true}
            label="Upload File"
            variant="contained"
            onClick={handleSubmit}
          />
        </Grid>
      </Grid>
      {isFileLinkUploadSupported && (
        <>
          <div
            style={{
              display: 'flex',
              marginTop: '0.5rem',
              alignItems: 'center',
            }}
          >
            <hr className={classes.separator} />
            <Typography style={{ margin: '0 0.5rem', fontWeight: 600 }}>
              OR
            </Typography>
            <hr className={classes.separator} />
          </div>
          <FileLinkInput />
        </>
      )}
      <hr className={classes.separator} />
      <Grid
        style={{
          width: '100%',
          display: 'flex',
          gap: '0.5rem',
          marginTop: '1rem',
        }}
        sx={{
          justifyContent: 'flex-end',
        }}
      >
        {!hideCancelButton && (
          <Button label={messages.CANCEL} variant="outlined" />
        )}

        {!hideReviewButton && (
          <Button
            label={messages.REVIEW_CONTRACT}
            variant="contained"
            width=""
            disabled={FileUploadStore.uploadedFile === ''}
          />
        )}
      </Grid>
    </>
  );
};

export default FileUploader;
